import React, { useState, useEffect, useRef, useMemo } from "react";
import { Search, X, Calendar, Hash } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useProject } from "../contexts/ProjectContext";
import { cardAPI } from "../utils/api";
import { stripHtmlTags } from "../utils/htmlUtils";

const AdvancedSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { projects } = useProject();

  // Are we currently inside a project? (drives whether we search cards)
  const currentProjectId = useMemo(() => {
    const match = location.pathname.match(/\/project\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
        setSearchResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Build project matches from already-loaded data (instant, no requests).
  const getProjectMatches = (query) => {
    const ql = query.toLowerCase();
    return (projects || [])
      .filter(
        (project) =>
          project.name?.toLowerCase().includes(ql) ||
          project.description?.toLowerCase().includes(ql) ||
          project.projectType?.toLowerCase().includes(ql) ||
          project.projectStatus?.toLowerCase().includes(ql)
      )
      .map((project) => ({
        id: project._id,
        type: "project",
        title: project.name,
        subtitle: project.description
          ? stripHtmlTags(project.description)
          : "No description",
        status: project.projectStatus,
        icon: Calendar,
        url: `/project/${project._id}`,
        metadata: {
          members: project.members?.length || 0,
          createdAt: project.createdAt,
          projectType: project.projectType,
          projectStatus: project.projectStatus,
        },
      }));
  };

  // Search: projects from memory (always) + cards from the CURRENT project only
  // (via the backend search endpoint). Off a project, no card requests.
  const performSearch = async (query, signal) => {
    const q = query.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }

    const projectMatches = getProjectMatches(q);
    // Show instant project matches right away.
    setSearchResults(projectMatches);

    if (!currentProjectId) return;

    setLoading(true);
    try {
      const response = await cardAPI.searchCards(currentProjectId, q, 20, {
        signal,
      });
      if (response.data?.success) {
        const projectName =
          (projects || []).find((p) => p._id === currentProjectId)?.name || "";
        const cardResults = (response.data.cards || []).map((card) => ({
          id: card._id,
          type: "card",
          title: card.title,
          subtitle: card.description
            ? stripHtmlTags(card.description)
            : "No description",
          status: card.statusLabel || card.status,
          statusColor: card.statusColor,
          icon: Hash,
          url: `/project/${currentProjectId}/card/${card._id}`,
          metadata: {
            project: projectName,
            assignees: card.assignees?.length || 0,
            priority: card.priority,
            dueDate: card.dueDate,
            cardNumber: card.cardNumber,
          },
        }));
        setSearchResults([...projectMatches, ...cardResults]);
      }
    } catch (error) {
      // Ignore aborted requests (superseded by a newer keystroke).
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return;
      }
      console.error("Card search error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Live, debounced search. A newer keystroke aborts the previous request.
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery, controller.signal);
    }, 350);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentProjectId]);

  const handleResultClick = (result) => {
    // Navigate to the result using React Router
    navigate(result.url);
    setIsOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const getStatusColor = (status, type) => {
    if (type === "project") {
      switch (status?.toLowerCase()) {
        case "new":
          return "bg-blue-100 text-blue-800";
        case "ongoing":
          return "bg-yellow-100 text-yellow-800";
        case "completed":
          return "bg-green-100 text-green-800";
        case "cancelled":
          return "bg-red-100 text-red-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    }

    switch (status?.toLowerCase()) {
      case "todo":
        return "bg-gray-100 text-gray-800";
      case "doing":
        return "bg-yellow-100 text-yellow-800";
      case "review":
        return "bg-purple-100 text-purple-800";
      case "done":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Map a column color (Column.color enum) to badge classes, so card results
  // are color-coded by their column — matching the board.
  const COLUMN_BADGE_CLASSES = {
    blue: "bg-blue-100 text-blue-800",
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
    purple: "bg-purple-100 text-purple-800",
    pink: "bg-pink-100 text-pink-800",
    indigo: "bg-indigo-100 text-indigo-800",
    orange: "bg-orange-100 text-orange-800",
    gray: "bg-gray-100 text-gray-800",
  };

  const getResultBadgeClasses = (result) => {
    if (result.type === "card" && result.statusColor) {
      return COLUMN_BADGE_CLASSES[result.statusColor] || COLUMN_BADGE_CLASSES.gray;
    }
    return getStatusColor(result.status, result.type);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="relative" ref={searchRef}>
      {/* Search Input */}
      <div className="relative">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
            <input
              type="text"
              placeholder="Search projects, cards (#27)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsOpen(true)}
              className="w-full md:w-[40rem] h-8 pl-10 pr-4 dark:bg-slate-900 dark:text-white dark:border-slate-600 border border-gray-500 rounded-lg placeholder:text-gray-500 placeholder:font-medium focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search Results */}
      {isOpen && (searchQuery || searchResults.length > 0) && (
        <div className="absolute top-full left-0 mt-2 w-full md:w-[40rem] bg-white rounded-lg shadow-xl border border-gray-200 z-[100] max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Searching...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="py-2">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {searchResults.length} result
                  {searchResults.length !== 1 ? "s" : ""} found
                </p>
              </div>
              {searchResults.map((result) => {
                const Icon = result.icon;
                return (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full flex items-start space-x-3 px-4 py-3 hover:bg-gray-50 text-left"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {result.title}
                        </p>
                        {result.metadata?.cardNumber !== undefined && (
                          <span className="text-xs font-medium text-gray-500">
                            #{result.metadata.cardNumber}
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getResultBadgeClasses(
                            result
                          )}`}
                        >
                          {result.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {result.subtitle}
                      </p>
                      {result.metadata && (
                        <div className="flex items-center space-x-4 mt-1">
                          {result.metadata.cardNumber !== undefined && (
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded ${getResultBadgeClasses(
                                result
                              )}`}
                            >
                              #{result.metadata.cardNumber}
                            </span>
                          )}
                          {result.metadata.project && (
                            <span className="text-xs text-gray-400">
                              Project: {result.metadata.project}
                            </span>
                          )}
                          {result.metadata.members !== undefined && (
                            <span className="text-xs text-gray-400">
                              {result.metadata.members} members
                            </span>
                          )}
                          {result.metadata.assignees !== undefined && (
                            <span className="text-xs text-gray-400">
                              {result.metadata.assignees} assignees
                            </span>
                          )}
                          {result.metadata.dueDate && (
                            <span className="text-xs text-gray-400">
                              Due: {formatDate(result.metadata.dueDate)}
                            </span>
                          )}
                          {result.metadata.projectType && (
                            <span className="text-xs text-gray-400">
                              Type: {result.metadata.projectType}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : searchQuery ? (
            <div className="p-4 text-center">
              <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No results found</p>
              <p className="text-xs text-gray-400 mt-1">
                Try different keywords or check your spelling
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
